import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FUNNEL_STAGES, tomorrowISO } from "@/dealership/lib/funnel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Activity as ActivityIcon } from "lucide-react";

export default function LogActivityDialog({ open, onOpenChange, presetLead = null, activity = null, onSaved }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const editing = !!activity;
  const [form, setForm] = useState(null);

  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await api.get("/leads")).data,
    enabled: open && !presetLead && !editing,
  });
  const { data: team = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => (await api.get("/team-members")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (activity) {
      setForm({
        lead_id: activity.lead_id, lead_name: activity.lead_name,
        type: activity.type, notes: activity.notes || "",
        assignee_id: activity.assignee_id || "",
        follow_up_date: activity.follow_up_date || tomorrowISO(),
        noFollow: !activity.follow_up_date,
      });
    } else {
      setForm({
        lead_id: presetLead?.id || "", lead_name: presetLead?.name || "",
        type: "Initial Contact", notes: "",
        assignee_id: user?.id || "",
        follow_up_date: tomorrowISO(), noFollow: false,
      });
    }
  }, [open, activity, presetLead]); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        lead_id: form.lead_id, lead_name: form.lead_name, type: form.type,
        notes: form.notes, assignee_id: form.assignee_id || null,
        follow_up_date: form.noFollow ? "" : form.follow_up_date,
      };
      if (editing) return (await api.put(`/activities/${activity.id}`, body)).data;
      return (await api.post("/activities", body)).data;
    },
    onSuccess: () => {
      ["activities", "notifications", "notif-count", "dashboard"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] }));
      toast.success(editing ? "Activity updated" : "Activity logged");
      onOpenChange(false);
      onSaved && onSaved();
    },
    onError: () => toast.error("Failed to save activity"),
  });

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ActivityIcon className="h-5 w-5 text-primary" /> {editing ? "Edit Activity" : "Log Activity"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {presetLead || editing ? (
            <div className="space-y-2">
              <Label className="label-mono">Lead</Label>
              <div className="h-10 flex items-center px-3 rounded-md bg-secondary text-sm font-medium">{form.lead_name}</div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="label-mono">Lead *</Label>
              <Select value={form.lead_id} onValueChange={(v) => { const l = leads.find((x) => x.id === v); set("lead_id", v); set("lead_name", l?.name || ""); }}>
                <SelectTrigger data-testid="activity-lead-select"><SelectValue placeholder="Select a lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.vehicle_interest ? ` · ${l.vehicle_interest}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="label-mono">Stage / Activity Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FUNNEL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="label-mono">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="What happened on this touchpoint?" data-testid="activity-notes-input" />
          </div>

          <div className="space-y-2">
            <Label className="label-mono">Assigned Rep</Label>
            <Select value={form.assignee_id} onValueChange={(v) => set("assignee_id", v)}>
              <SelectTrigger data-testid="activity-assignee-select"><SelectValue placeholder="Select rep" /></SelectTrigger>
              <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.role === "admin" ? " (admin)" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="rounded-md border border-border p-3 space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={!form.noFollow} onChange={(e) => set("noFollow", !e.target.checked)} data-testid="activity-followup-toggle" />
              Schedule next follow-up
            </label>
            {!form.noFollow && (
              <div className="space-y-2">
                <Label className="label-mono">Next follow-up date</Label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="activity-followup-date" />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={!form.lead_id || saveMut.isPending} data-testid="save-activity-button">
            {editing ? "Update" : "Log Activity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
